import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { Label } from '../../../components/ui/label';
import { Megaphone, AlertCircle } from 'lucide-react';
import { useEventsModule } from '../../../context/EventsModuleContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthConfig = () => {
  const token = localStorage.getItem('admin_token');
  return {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    withCredentials: true,
  };
};

const ModulesManager = () => {
  const { refresh } = useEventsModule();
  const [eventsEnabled, setEventsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchSetting = async () => {
      try {
        const res = await axios.get(`${API}/admin/modules/events`, getAuthConfig());
        setEventsEnabled(res.data.enabled !== false);
      } catch (e) {
        toast.error('Kunde inte hämta modul-inställningar');
      } finally {
        setLoading(false);
      }
    };
    fetchSetting();
  }, []);

  const handleToggle = async (checked) => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/modules/events`,
        { enabled: checked },
        getAuthConfig()
      );
      setEventsEnabled(res.data.enabled);
      await refresh();
      if (!checked) {
        toast.success(`Händelser-modulen avaktiverad. ${res.data.purged_event_count || 0} händelser togs bort.`);
      } else {
        toast.success('Händelser-modulen aktiverad. Klicka "Hämta händelser" för att synka.');
      }
    } catch (e) {
      toast.error('Kunde inte uppdatera modulinställningen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Laddar moduler…</div>;
  }

  return (
    <div className="space-y-6" data-testid="modules-manager">
      <div>
        <h2 className="text-2xl font-serif font-bold text-gray-900 mb-1">Moduler</h2>
        <p className="text-sm text-gray-600">
          Aktivera eller avaktivera valfria moduler. Mässor är alltid aktiva.
        </p>
      </div>

      <Card className="border-0 shadow-lg card-hover">
        <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50">
          <CardTitle className="flex items-center gap-3">
            <Megaphone className="w-6 h-6 text-yellow-600" />
            <span>Händelser / Kungörelser</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="events-module-toggle" className="text-base font-medium">
                Aktivera Händelser-modulen
              </Label>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                När avaktiverad: iCal-händelsesynk pausas, kalendersektioner döljs på publika sidor,
                och menyalternativ ändras. <strong>Mässor påverkas inte</strong> och fortsätter
                synka som vanligt.
              </p>
            </div>
            <Switch
              id="events-module-toggle"
              checked={eventsEnabled}
              onCheckedChange={handleToggle}
              disabled={saving}
              data-testid="events-module-toggle"
            />
          </div>

          {!eventsEnabled && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                Modulen är avaktiverad. Tidigare synkade händelser är borttagna från databasen.
                Återaktivera och kör en synk för att hämta dem igen.
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ModulesManager;
